import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, ArrowRight, Building2, Globe2, Sparkles, ShieldCheck, Users, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const COUNTRY_CONFIG = {
  in: { code: 'IN', label: 'India', price: '₹2,499', period: '/yr', flag: '🇮🇳', currency: 'INR', amount: 2499 },
  uk: { code: 'UK', label: 'United Kingdom', price: '£29', period: '/mo', flag: '🇬🇧', currency: 'GBP', amount: 29 },
  ca: { code: 'CA', label: 'Canada', price: 'C$49', period: '/mo', flag: '🇨🇦', currency: 'CAD', amount: 49 },
};

export default function AgentLandingPage() {
  const { country: countryParam } = useParams();
  const [searchParams] = useSearchParams();
  const ref = searchParams.get('ref') || '';

  const cfg = useMemo(() => COUNTRY_CONFIG[(countryParam || 'in').toLowerCase()] || COUNTRY_CONFIG.in, [countryParam]);

  const [form, setForm] = useState({ name: '', email: '', phone: '', city: '', firm: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = `Verified Power Partner — Shopperden ${cfg.label}`;
  }, [cfg]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.name) {
      toast.error('Name and email are required');
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/partner/public/reserve`, {
        ...form,
        country: cfg.code,
        ref: ref || undefined,
        origin_url: window.location.origin,
      });
      if (r.data?.url) {
        window.location.href = r.data.url;
      } else if (r.data?.already_active) {
        toast.success('You\'re already a Verified Partner');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start checkout');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#0A0A0C', color: '#FAFAFA' }} data-testid="agent-landing-page">
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">

          {/* Left: pitch */}
          <div>
            <Link to="/" className="text-sm tracking-widest text-white/60 hover:text-[#D4AF37]">← SHOPPERDEN</Link>
            <p className="mt-6 text-[11px] tracking-[0.3em] text-[#D4AF37]">VERIFIED POWER PARTNER · {cfg.flag} {cfg.label.toUpperCase()}</p>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl mt-3 leading-tight">
              Get NRI buyers<br/>
              <span className="text-[#D4AF37]">delivered to your phone.</span>
            </h1>
            <p className="text-white/70 text-base sm:text-lg mt-6 leading-relaxed max-w-xl">
              Shopperden is the AI Real Estate Network for UK · India · Canada. We aggregate verified
              NRI buyers and route every match directly to {cfg.label} Verified Partners — no cold
              outreach, no commission split, no broker mark-up.
            </p>

            <ul className="mt-10 space-y-4 max-w-xl">
              {[
                { icon: <Globe2 className="h-5 w-5" />, text: 'NRI Buyer Match Feed — buyers from UK + Canada looking for India property are routed first to you' },
                { icon: <Sparkles className="h-5 w-5" />, text: 'Bulk Listing Sync — upload 200 listings via CSV in 30 seconds, all auto-PII-scrubbed' },
                { icon: <ShieldCheck className="h-5 w-5" />, text: 'Verified Partner Badge — gold trust mark on every listing card + WhatsApp DP' },
                { icon: <Building2 className="h-5 w-5" />, text: 'Pulse Engine analytics — listing-level demand score, refreshed weekly' },
                { icon: <Users className="h-5 w-5" />, text: 'Affiliate Loop — refer 3 peers, your membership is effectively free (₹750 per referral)' },
              ].map((b, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="text-[#D4AF37] mt-0.5">{b.icon}</span>
                  <span className="text-white/80 text-[15px] leading-relaxed">{b.text}</span>
                </li>
              ))}
            </ul>

            <p className="mt-12 text-xs text-white/40 max-w-xl">
              Shopperden is an introduction service — not an estate agent or brokerage. We deliver
              verified buyer matches and Pulse intelligence; final negotiations remain between you
              and the buyer.
            </p>
          </div>

          {/* Right: form card */}
          <div className="lg:sticky lg:top-12">
            <div style={{ background: '#121215', border: '1px solid #2E2E34', padding: '32px' }}>
              <p className="text-[11px] tracking-[0.3em] text-[#D4AF37]">RESERVE YOUR SLOT</p>
              <div className="flex items-baseline gap-2 mt-3">
                <span className="font-['Playfair_Display'] text-5xl text-white">{cfg.price}</span>
                <span className="text-white/60">{cfg.period}</span>
              </div>
              <p className="text-white/50 text-xs mt-1">No commitment · cancel anytime · {cfg.currency} via Stripe</p>

              <form onSubmit={submit} className="mt-6 space-y-3" data-testid="agent-landing-form">
                <input
                  required
                  type="text"
                  placeholder="Your full name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-3 text-sm"
                  style={{ background: '#0A0A0C', border: '1px solid #2E2E34', color: '#FAFAFA' }}
                  data-testid="agent-form-name"
                />
                <input
                  required
                  type="email"
                  placeholder="Your email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-3 text-sm"
                  style={{ background: '#0A0A0C', border: '1px solid #2E2E34', color: '#FAFAFA' }}
                  data-testid="agent-form-email"
                />
                <input
                  type="tel"
                  placeholder={cfg.code === 'IN' ? 'WhatsApp number (+91...)' : 'Phone (optional)'}
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-4 py-3 text-sm"
                  style={{ background: '#0A0A0C', border: '1px solid #2E2E34', color: '#FAFAFA' }}
                  data-testid="agent-form-phone"
                />
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    placeholder="City"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    className="w-full px-4 py-3 text-sm"
                    style={{ background: '#0A0A0C', border: '1px solid #2E2E34', color: '#FAFAFA' }}
                    data-testid="agent-form-city"
                  />
                  <input
                    type="text"
                    placeholder="Agency name"
                    value={form.firm}
                    onChange={(e) => setForm({ ...form, firm: e.target.value })}
                    className="w-full px-4 py-3 text-sm"
                    style={{ background: '#0A0A0C', border: '1px solid #2E2E34', color: '#FAFAFA' }}
                    data-testid="agent-form-firm"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 text-sm font-bold tracking-widest mt-2 flex items-center justify-center gap-2"
                  style={{ background: '#D4AF37', color: '#0A0A0C' }}
                  data-testid="agent-form-submit"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                  RESERVE MY SLOT — {cfg.price}
                </button>
              </form>

              <div className="mt-6 pt-6" style={{ borderTop: '1px solid #2E2E34' }}>
                <div className="flex items-center gap-2 text-xs text-white/60">
                  <CheckCircle2 className="h-3.5 w-3.5 text-[#D4AF37]" />
                  <span>Secure checkout by Stripe</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-white/60 mt-2">
                  <CheckCircle2 className="h-3.5 w-3.5 text-[#D4AF37]" />
                  <span>Welcome kit + bulk-upload link emailed instantly</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-white/60 mt-2">
                  <CheckCircle2 className="h-3.5 w-3.5 text-[#D4AF37]" />
                  <span>30-day full refund if no buyer match</span>
                </div>
              </div>
              {ref && (
                <p className="mt-4 text-[11px] text-[#D4AF37]/70">Referred by code: <code>{ref}</code></p>
              )}
            </div>

            {/* Country switch */}
            <div className="mt-4 flex flex-wrap gap-2 justify-center">
              {Object.entries(COUNTRY_CONFIG).map(([slug, c]) => (
                <Link
                  key={slug}
                  to={`/agents/${slug}${ref ? `?ref=${ref}` : ''}`}
                  className={`text-xs px-3 py-1.5 ${cfg.code === c.code ? 'text-[#D4AF37]' : 'text-white/50 hover:text-white/80'}`}
                  data-testid={`agent-country-${slug}`}
                >
                  {c.flag} {c.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
